// Flash Countdown Timer con Auto-reset y Fecha de Inicio
class FlashCountdown {
  constructor(element) {
    this.element = element;
    this.repeatInterval = parseInt(element.dataset.repeatInterval) || 24; // Horas
    this.blockId = element.dataset.blockId;
    this.startDateStr = element.dataset.startDate ? element.dataset.startDate.trim() : '';
    this.storageKey = `flash_countdown_${this.blockId}`;
    this.startStorageKey = `flash_countdown_started_${this.blockId}`;
    this.configKey = `flash_countdown_config_${this.blockId}`;
    
    this.daysEl = element.querySelector('[data-days]');
    this.hoursEl = element.querySelector('[data-hours]');
    this.minutesEl = element.querySelector('[data-minutes]');
    this.secondsEl = element.querySelector('[data-seconds]');
    
    this.interval = null;
    
    // Verificar si la configuración cambió
    this.checkConfigChange();
    
    this.hasStarted = this.checkIfStarted();
    this.endDate = this.getEndDate();
    this.init();
  }
  
  checkConfigChange() {
    const currentConfig = `${this.repeatInterval}_${this.startDateStr}`;
    const storedConfig = localStorage.getItem(this.configKey);
    
    // Si la configuración cambió, limpiar localStorage
    if (storedConfig !== currentConfig) {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.startStorageKey);
      localStorage.setItem(this.configKey, currentConfig);
    }
  }
  
  checkIfStarted() {
    // Si no hay fecha de inicio configurada, ya empezó inmediatamente
    if (!this.startDateStr) {
      localStorage.setItem(this.startStorageKey, 'true');
      return true;
    }
    
    // Verificar si ya se marcó como iniciado en localStorage
    const started = localStorage.getItem(this.startStorageKey);
    if (started === 'true') {
      return true;
    }
    
    // Verificar si la fecha de inicio ya pasó
    const startDate = new Date(this.startDateStr.replace(' ', 'T')).getTime();
    const now = new Date().getTime();
    
    if (now >= startDate) {
      localStorage.setItem(this.startStorageKey, 'true');
      return true;
    }
    
    return false;
  }
  
  getEndDate() {
    const intervalMs = this.repeatInterval * 60 * 60 * 1000; // Duración en milisegundos
    
    // Si aún no ha comenzado y hay fecha de inicio configurada
    if (!this.hasStarted && this.startDateStr) {
      const startDate = new Date(this.startDateStr.replace(' ', 'T')).getTime();
      const now = new Date().getTime();
      
      // Si la fecha de inicio es futura, el contador termina después de la duración desde ese inicio
      if (startDate > now) {
        return startDate + intervalMs;
      }
    }
    
    // Intentar obtener la fecha guardada en localStorage
    const stored = localStorage.getItem(this.storageKey);
    
    if (stored) {
      const storedTime = parseInt(stored);
      const now = new Date().getTime();
      
      // Si la fecha guardada aún es válida (en el futuro), usarla
      if (storedTime > now) {
        return storedTime;
      }
    }
    
    // Si no hay fecha guardada o ya expiró, calcular una nueva desde ahora
    return this.calculateNextEndDate();
  }
  
  calculateNextEndDate() {
    const now = new Date().getTime();
    const intervalMs = this.repeatInterval * 60 * 60 * 1000; // Duración del contador
    const endDate = now + intervalMs;
    
    // Guardar en localStorage
    localStorage.setItem(this.storageKey, endDate.toString());
    
    return endDate;
  }
  
  init() {
    // Debug info en consola
    console.log('Flash Countdown iniciado:', {
      blockId: this.blockId,
      duration: `${this.repeatInterval} horas`,
      startDate: this.startDateStr || 'Inicia inmediatamente',
      hasStarted: this.hasStarted,
      endDate: new Date(this.endDate).toLocaleString()
    });
    
    this.updateCountdown();
    this.interval = setInterval(() => this.updateCountdown(), 1000);
  }
  
  updateCountdown() {
    const now = new Date().getTime();
    
    // FASE 1: Si hay fecha de inicio configurada y aún no ha llegado
    if (!this.hasStarted && this.startDateStr) {
      const startDate = new Date(this.startDateStr.replace(' ', 'T')).getTime();
      
      // Si la fecha de inicio aún está en el futuro
      if (startDate > now) {
        const distance = startDate - now;
        
        // Mostrar cuenta regresiva hasta el inicio
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        this.daysEl.textContent = this.formatNumber(days);
        this.hoursEl.textContent = this.formatNumber(hours);
        this.minutesEl.textContent = this.formatNumber(minutes);
        this.secondsEl.textContent = this.formatNumber(seconds);
        return;
      } else {
        // La fecha de inicio ya pasó, marcar como iniciado
        this.hasStarted = true;
        localStorage.setItem(this.startStorageKey, 'true');
        this.endDate = this.calculateNextEndDate();
        return;
      }
    }
    
    // FASE 2: Cuenta regresiva de la duración configurada
    const distance = this.endDate - now;
    
    // Si el contador llegó a cero, reiniciar automáticamente
    if (distance <= 0) {
      this.endDate = this.calculateNextEndDate();
      // Actualizar en el próximo tick para evitar mostrar negativos
      return;
    }
    
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    
    this.daysEl.textContent = this.formatNumber(days);
    this.hoursEl.textContent = this.formatNumber(hours);
    this.minutesEl.textContent = this.formatNumber(minutes);
    this.secondsEl.textContent = this.formatNumber(seconds);
  }
  
  formatNumber(num) {
    return num < 10 ? '0' + num : num.toString();
  }
  
  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
  
  // Método para reiniciar manualmente (útil para debugging)
  reset() {
    console.log('Reiniciando contador...');
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.startStorageKey);
    this.hasStarted = this.checkIfStarted();
    this.endDate = this.getEndDate();
    console.log('Contador reiniciado:', {
      duration: `${this.repeatInterval} horas`,
      startDate: this.startDateStr || 'Inicia inmediatamente',
      hasStarted: this.hasStarted,
      newEndDate: new Date(this.endDate).toLocaleString()
    });
  }
}

// Almacenar contadores globalmente para debugging
window.flashCountdowns = window.flashCountdowns || [];

// Inicializar todos los contadores en la página
document.addEventListener('DOMContentLoaded', () => {
  const countdownElements = document.querySelectorAll('.m-flash-countdown-block__timer[data-repeat-interval]');
  
  countdownElements.forEach(element => {
    const countdown = new FlashCountdown(element);
    window.flashCountdowns.push(countdown);
  });
  
  console.log(`${window.flashCountdowns.length} contadores flash inicializados`);
  console.log('Para reiniciar todos: window.flashCountdowns.forEach(c => c.reset())');
  
  // Limpiar al salir de la página
  window.addEventListener('beforeunload', () => {
    window.flashCountdowns.forEach(countdown => countdown.destroy());
  });
});

// Soporte para Shopify Theme Editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const countdownElements = event.target.querySelectorAll('.m-flash-countdown-block__timer[data-repeat-interval]');
    countdownElements.forEach(element => {
      const countdown = new FlashCountdown(element);
      window.flashCountdowns.push(countdown);
    });
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    // Limpiar contadores del bloque eliminado
    const countdownElements = event.target.querySelectorAll('.m-flash-countdown-block__timer[data-repeat-interval]');
    countdownElements.forEach(element => {
      const blockId = element.dataset.blockId;
      const index = window.flashCountdowns.findIndex(c => c.blockId === blockId);
      if (index !== -1) {
        window.flashCountdowns[index].destroy();
        window.flashCountdowns.splice(index, 1);
      }
    });
  });
}
